import * as React from 'react';
import { useDispatch } from 'react-redux';
import { debounce } from 'throttle-debounce';

import { TThunkDispatch } from '../../../../../../types/redux/thunk';
import { useOfferContext } from '../../../../../../utils/offerContext';
import { getFirstImageFromOffer } from '../../../../../../utils/offerImage';
import { trackCloseFullscreenGallery, trackOpenFullscreenGallery } from '../tracking/fullscreenGalleryTracking';
import { deleteOffer } from '../../../../../../actions/compare';
import { FAVORITE_DEBOUNCE_DELAY } from '../../../../../../constants/delays';
import { addFavorite, deleteFavorite } from '../../../../../../actions/favorite';

export interface IHeaderControls {
  onClick: () => void;
  onDelete: () => void;
  onFavoriteChange: () => void;
  onGalleryClose: () => void;
  galleryOpened: boolean;
  favorite: boolean;
  published: boolean;
  imageUrl: string;
  title: string;
}

export function useHeaderControls(): IHeaderControls {
  const [galleryOpened, setGalleryOpened] = React.useState(false);

  const dispatch = useDispatch<TThunkDispatch>();
  const offerContext = useOfferContext();
  const {
    id,
    title,
    images,
    published,
    isFavorite,
    analytics: { gaLabel },
  } = offerContext;
  const { thumbnailUrl, type: imageType } = getFirstImageFromOffer(offerContext);

  const handleItemClick = React.useCallback(() => {
    if (!images.length || !published) {
      return;
    }

    setGalleryOpened(true);
    trackOpenFullscreenGallery({ imageType, offerId: id });
  }, [setGalleryOpened, images, published, imageType, id]);

  const handleGalleryClose = React.useCallback(() => {
    setGalleryOpened(false);
    trackCloseFullscreenGallery({ offerId: id });
  }, [setGalleryOpened, id]);

  const handleDelete = React.useCallback(async () => {
    await dispatch(deleteOffer({ offerId: id, first: true }));
  }, [dispatch, id]);

  const handleFavoriteChange = React.useCallback(
    debounce(FAVORITE_DEBOUNCE_DELAY, () => {
      if (isFavorite) {
        dispatch(deleteFavorite({ offerId: id, label: gaLabel }));
      } else {
        dispatch(addFavorite({ offerId: id, label: gaLabel }));
      }
    }),
    [dispatch, id, isFavorite, gaLabel],
  );

  return {
    title,
    imageUrl: thumbnailUrl,
    onClick: handleItemClick,
    onDelete: handleDelete,
    onFavoriteChange: handleFavoriteChange,
    onGalleryClose: handleGalleryClose,
    favorite: isFavorite,
    published,
    galleryOpened,
  };
}
